import util from 'util'
import Promise from 'bluebird'
import moment from 'moment'
import { vals } from '../../util'
import api from '../../axios_api'
import _ from 'lodash'
import { createCompany, patchCompany } from '../../models/Company'
import router from '../../main'

const COMPANY_HEADERS = ['sector', 'geography', 'stage', 'status', 'name', 'rating']
const WRITE_INTERVAL = 5 // cycle counts (5 cycles per write ~5s)

class CompanyModule {
  constructor() {
    this.server_data = []
    this.local_cache = []
    this._write_timer = 0
    this._processing_cycle = false
  }

  companyData(state) {

  }

  getters() {
    return {

    }
  }
}

/**
 * Company {
 * _id: mongo id
 * name: company name
 * status: Active, Out of Business, etc
 * last_funding: Last round of funding
 * stage: Seed, Pre-Seed, Series A, etc
 * sector: company sector (Fintech, etc)
 * geography: location of headquarters (San Francisco, etc)
 * description: long-form description of company
 * logo_url: url of company logo
 * ucid: public-facing unique id of the company
 * }
 */

// #region State
const state = {
  server_data: [],
  local_cache: [],
  _write_timer: 0,
  _processing_cycle: false

}
// #endregion

// #region Getters
const getters = {
  companyData(state) {
    // need to merge local_cache with company data
    let retval = _.map(state.server_data, data => {
      let cached = _.reduce(state.local_cache, (memo, cache) => {
        if (cache._id == data._id) return cache
        return memo
      }, {})
      // return all properties that don't start with '_'
      return _.omitBy(_.assign(data, cached), function (v, k) { return _.startsWith(k, '_') })
    })
    return retval
  },
  geographyData(state) {
    return [...new Set(state.server_data.map(item => item.geography))]
  },
  companyDataFull(state) {
    // need to merge local_cache with company data
    let retval = _.map(state.server_data, data => {
      let cached = _.reduce(state.local_cache, (memo, cache) => {
        if (cache._id == data._id) return cache
        return memo
      }, {})
      // return all properties that don't start with '_'
      return _.assign(data, cached)
    })
    return retval
  },
  isDirty(state) {
    return !_.isNil(state.local_cache) && !_.isEmpty(state.local_cache)
  },
  getCompany: (state) => (id) => {
    // find the company by either _id or ucid
    let cache = _.map(state.server_data, data => {
      let local = _.reduce(state.local_cache, (memo, l) => {
        if (l._id == data._id) return l
        return memo
      }, {})
      // return all properties
      return _.assign(data, local)
    })
    // go through the cache and find our item
    return _.reduce(cache, (memo, c) => {
      return (c._id == id || c.ucid == id) ? c : memo
    }, {})
  }
}
// #endregion

// #region Mutators
const mutations = {
  initialize(state) {
    state.server_data = []
    state.local_cache = []
    state._write_timer = 0
    state._processing_cycle = false
  },
  company(state, payload) {
    state.server_data = payload.server_data
  },
  merge(state, payload) {
    payload = payload.companies
    //if (!_.isArray(payload)) payload = _.without([payload], undefined, null)
    // merge what was returned from the server with what we have in server
    state.server_data = _.without(_.map(state.server_data, data => {
      if (!data) return data
      let index = _.findIndex(payload, function (p) { return p._id == data._id })
      if (index >= 0) {
        let item = _.first(_.pullAt(payload, index))
        if (_.lt(item.__v, data.__v)) return data
        return _.omit(_.assign(data, item), ['_sent', '_dirty'])
      }
      return data
    }), null, undefined)
    // add remaining updates
    state.server_data = _.concat(state.server_data, payload)
  },
  update(state, payload) {
    // go through the companies and merge any data
    let companies = payload.companies
    if (!_.isArray(companies)) companies = [companies]
    // remove any non-company data
    companies = _.map(companies, item => {
      item = _.omitBy(item, function (v, k) { return _.indexOf(COMPANY_HEADERS, k) < 0 })
      if (_.keys(item).length < 2) return null
      return item
    })
    companies = _.without(companies, null, undefined)
    if (companies.length < 1) return // ignore any updates not pertinent to this collection
    // get our _id for the company
    let update = _.without(_.map(payload, item => {
      let company = _.find(state.server_data, function (d) { return d.name == item.name })
      if (company) return _.assign({}, item, { _id: company._id, __v: company.__v })
    }), undefined, null)
    if (!_.isEmpty(update)) {
      state.local_cache = _.map(state.local_cache, cached => {
        let index = _.findIndex(update, function (u) { return u._id == cached._id })
        if (index >= 0) {
          let cpny = _.first(_.pullAt(update, index))
          return _.merge(cached, cpny, { _dirty: moment() })
        }
        return cached
      })
      // add remaining updates, that were not merged
      state.local_cache = _.concat(state.local_cache, update)
    }
  },
  clean(state, payload) {
    // payload is data written to the server
    // remove any data older than _sent from the local cache
    state.local_cache = _.without(_.map(state.local_cache, cached => {
      let item = _.find(payload, function (p) { return p._id == cached._id })
      if (!item || item._sent < cached._dirty) return cached // return only if dont have it in the payload or it is newer
    }), undefined, null)
  },
  remove(state, ucid) {
    // if (_.isNil(ucid) || _.isEmpty(ucid)) return
    // remove the company from local and server data
    state.server_data = _.map(state.server_data, gram => {
      if (gram.ucid && gram.ucid == ucid) return null
      return gram
    })
    state.server_data = _.without(state.server_data, null, undefined)

    state.local_cache = _.map(state.local_cache, gram => {
      if (gram.ucid && gram.ucid == ucid) return null
      return gram
    })
    state.local_cache = _.without(state.local_cache, null, undefined)
  }
}
// #endregion

// #region Actions
const actions = {
  start({ state }) {
    state._write_timer = 0
    state._processing_cycle = false
  },
  stop({ state, commit }) {
    // clean up
    commit('initialize')
    state._processing_cycle = false
  },
  cycle({ state, getters, dispatch, rootGetters }) {
    state._write_timer += 1
    if (!state._processing_cycle && rootGetters['user/hasToken']) {
      state._processing_cycle = true
      // check to see if we need to do any writes
      if (getters['isDirty'] && state._write_timer >= WRITE_INTERVAL) {
        state._write_timer = 0
        // write to our server
        return dispatch('apiPatchCompany')
          .then((fail) => {
            // portfolio is in charge if fetching data for company info
            state._processing_cycle = false
          })
          .catch(err => {
            console.error(`Error writting: ${err}`)
            state._processing_cycle = false
          })
      } else {
        state._processing_cycle = false // not doing anything
      }
    }
  },
  apiFetchCompany({ commit }, payload) {
    let params = payload || {}
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/company`;

    return api.get(url, { params })
      .then(res => {
        // if we are overwriting, stomp on local stuff
        if (payload && payload.overwrite) {
          // TODO: overwrite
        } else if (!_.isNil(res.data.encrypt.data) && !_.isEmpty(res.data.encrypt.data)) {
          // merge
          commit('merge', { companies: res.data.encrypt.data })
        }
        return Promise.resolve(res.data.encrypt.data)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  apiPostCompanyInst({ rootGetters }, payload) {
    // payload should be a company instance
    let currentRole = rootGetters['role/current_role'];
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/portfolio/company`;
    return api.post(url, { encrypt: { data: payload } })
      .then(res => {
        // res should be the updated company info
        return Promise.resolve(res.data.encrypt)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  apiPatchCompanyInst({ }, payload) {
    // payload should be a Company instance
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/company`;
    return api.patch(url, { encrypt: { data: payload } })
      .then(res => {
        return Promise.resolve(res.data.encrypt)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  apiPatchCompanyInstByWorkspace({ }, payload) {
    // payload should be a Company instance
    let url = `${router.currentRoute.params.account_id}/${payload.workspace_id}/company`;
    return api.patch(url, { encrypt: { data: payload } })
      .then(res => {
        return Promise.resolve(res.data.encrypt)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  },
  apiPatchCompany({ state, commit }) {
    // take our current local cache and write it to server
    return new Promise((resolve, reject) => {
      let payload = _.map(state.local_cache, item => {
        // add a _sent item to the cache
        return _.omit(_.assign(item, { _sent: moment() }), ['_sent', '_dirty', 'name'])
      })
      let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/company`;

      return api.patch(url, { 'encrypt': { data: payload } })
        .then(res => {
          // merge our successes
          if (!_.isNil(res.data.encrypt.success) && !_.isEmpty(res.data.encrypt.success)) {
            commit('merge', _.clone(res.data.encrypt.success))
            // clean up our local cache
            commit('clean', _.clone(res.data.encrypt.success))
          } else {
            // clean up our bad updates
            commit('clean', _.clone(res.data.encrypt.fail))
          }
          // return fails
          resolve(res.data.encrypt.fail || [])
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  apiDeleteCompany({ state, commit, rootGetters }, payload) {
    // payload should be the company (needing at least the ucid)
    if (_.isNil(payload) || _.isEmpty(payload) || !payload.ucid) {
      // can't delete nothing...
      return Promise.reject({ error: 'No company info to delete.' })
    }
    let currentRole = rootGetters['role/current_role'];
    let url = `${router.currentRoute.params.account_id}/${router.currentRoute.params.workspace_id}/company/${payload.ucid}`;
    // if (currentRole && currentRole._id) {
    //   url = `/company/${payload.ucid}`?account=${currentRole.account}`; 
    //  } else  `/company/${payload.ucid}`
    return api.delete(url)
      .then(res => {
        // should be success, remove the company from local
        commit('remove', payload.ucid)
        return Promise.resolve({})
      })
      .catch(err => {
        // error returned, delete did not happen (likely)
        return Promise.reject({ error: err })
      })
  }
}
// #endregion

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}